import { useEffect, useState } from "react";
import { Star, ArrowUpRight } from "lucide-react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import ReviewCard from "../shared/ReviewCard";
import { reviewService } from "../../services/reviewService";

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  useEffect(() => {
    reviewService.summary().then(setData);
  }, []);
  if (!data) return null;

  return (
    <section
      className="bg-ink py-24 md:py-32 border-t border-charcoal/50"
      data-testid="google-reviews"
    >
      <div className="container-emilio">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12">
          <div className="lg:col-span-7">
            <Eyebrow>Google Reviews</Eyebrow>
            <RevealText
              as="h2"
              text={"Trusted by customers\nacross Singapore."}
              className="mt-4 font-display font-extrabold text-white text-display"
            />
          </div>
          <div className="lg:col-span-5 lg:justify-self-end">
            <div className="border border-charcoal p-6 bg-charcoal/30 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white grid place-items-center">
                <GoogleG />
              </div>
              <div>
                <div className="text-mutedtext text-xs uppercase tracking-[0.16em]">
                  Google Rating
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-white font-display font-extrabold text-2xl leading-none">
                    {data.rating}
                  </span>
                  <span className="flex text-emerald">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </span>
                </div>
                <div className="text-mutedtext text-xs mt-1">
                  {data.reviewCount}+ reviews
                  {data.sample && <span className="text-emerald ml-2">· Sample preview</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {(data.reviews || []).map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>

        {/*
          Google's API releases only five reviews at a time and does not let us
          choose which five, so the count above will always be far larger than
          the number of cards below it. Without a way through to the full
          listing that reads as a contradiction rather than as proof.
        */}
        {data.googleMapsUrl && (
          <div className="mt-10">
            <a
              href={data.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-all"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-emerald hover:text-emerald text-white px-6 py-3 text-sm font-semibold transition-colors"
            >
              Read all {data.reviewCount} reviews on Google
              <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function GoogleG() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.26 1.06-3.72 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.85 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.67-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.2 1.65l3.15-3.15C17.45 2.14 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}
